import { Injectable, signal, computed } from '@angular/core';
import { environment } from '../../environments/environment';

const WARN_KEY        = 'vigilant-idle-warn-seconds';
const LOGOUT_KEY      = 'vigilant-idle-logout-seconds';
const ARROW_SCALE_KEY = 'vigilant-arrow-scale-miles';
const HOME_LAT_KEY    = 'vigilant-home-lat';
const HOME_LNG_KEY    = 'vigilant-home-lng';
const THEME_MODE_KEY  = 'vigilant-theme-mode';
const COMBINE_DEVICES_KEY = 'vigilant-combine-devices';
const SYNC_PERIOD_KEY = 'vigilant-sync-period-seconds';
const MIN_SYNC_PERIOD_SECONDS = 5;
const ALERT_RETENTION_KEY = 'vigilant-alert-retention-hours';
const MIN_ALERT_RETENTION_HOURS = 1;

export type ThemeMode = 'light' | 'dark' | 'system';

function loadFloatOrNull(key: string): number | null {
  const raw = localStorage.getItem(key);
  if (raw == null) return null;
  const n = parseFloat(raw);
  return isNaN(n) ? null : n;
}

function loadThemeMode(): ThemeMode {
  const raw = localStorage.getItem(THEME_MODE_KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  readonly defaultWarnSecs       = environment.idleSecondsBeforeWarning;
  readonly defaultLogoutSecs     = environment.idleSecondsBeforeLogout;
  readonly defaultArrowScaleMiles = 10;
  readonly defaultSyncPeriodSeconds = 15;
  readonly defaultAlertRetentionHours = 10;

  readonly warningSecs = signal(
    parseInt(localStorage.getItem(WARN_KEY) ?? '', 10) || environment.idleSecondsBeforeWarning
  );

  readonly logoutAfterWarningSecs = signal(
    parseInt(localStorage.getItem(LOGOUT_KEY) ?? '', 10) || environment.idleSecondsBeforeLogout
  );

  readonly arrowScaleMiles = signal(
    parseInt(localStorage.getItem(ARROW_SCALE_KEY) ?? '', 10) || this.defaultArrowScaleMiles
  );

  /**
   * How often SyncService polls `POST /api/users/self/sync` once it's caught up
   * (`isComplete: true`) — lower it to pick up new locations/entity updates sooner.
   * Also used as the delay after the initial bootstrap loads settle before the very
   * first sync call fires. Clamped to a {@link MIN_SYNC_PERIOD_SECONDS} floor so the
   * endpoint can't be hammered by an accidental 0/negative value.
   */
  readonly syncPeriodSeconds = signal(
    Math.max(
      parseInt(localStorage.getItem(SYNC_PERIOD_KEY) ?? '', 10) || this.defaultSyncPeriodSeconds,
      MIN_SYNC_PERIOD_SECONDS,
    )
  );

  setSyncPeriodSeconds(v: number): void {
    const clamped = Math.max(v, MIN_SYNC_PERIOD_SECONDS);
    localStorage.setItem(SYNC_PERIOD_KEY, String(clamped));
    this.syncPeriodSeconds.set(clamped);
  }

  /**
   * How far back AlertsService's initial/no-cache fetch looks (`sincePublishedAt`) and how long
   * an alert is kept before being pruned from the live feed. TeamAlertsService mirrors this same
   * value for its own initial fetch/retention, per its class doc.
   */
  readonly alertRetentionHours = signal(
    Math.max(
      parseInt(localStorage.getItem(ALERT_RETENTION_KEY) ?? '', 10) || this.defaultAlertRetentionHours,
      MIN_ALERT_RETENTION_HOURS,
    )
  );

  setAlertRetentionHours(v: number): void {
    const clamped = Math.max(v, MIN_ALERT_RETENTION_HOURS);
    localStorage.setItem(ALERT_RETENTION_KEY, String(clamped));
    this.alertRetentionHours.set(clamped);
  }

  /** User-set "home" location — client-only preference, never sent to the API. */
  readonly homeLat = signal(loadFloatOrNull(HOME_LAT_KEY));
  readonly homeLng = signal(loadFloatOrNull(HOME_LNG_KEY));
  readonly hasHome = computed(() => this.homeLat() != null && this.homeLng() != null);

  setHome(lat: number, lng: number): void {
    localStorage.setItem(HOME_LAT_KEY, String(lat));
    localStorage.setItem(HOME_LNG_KEY, String(lng));
    this.homeLat.set(lat);
    this.homeLng.set(lng);
  }

  clearHome(): void {
    localStorage.removeItem(HOME_LAT_KEY);
    localStorage.removeItem(HOME_LNG_KEY);
    this.homeLat.set(null);
    this.homeLng.set(null);
  }

  /** Light/dark/system appearance preference, applied globally by the root component. */
  readonly themeMode = signal<ThemeMode>(loadThemeMode());

  setThemeMode(v: ThemeMode): void {
    localStorage.setItem(THEME_MODE_KEY, v);
    this.themeMode.set(v);
  }

  /**
   * Show only each user's newest device per OS/device-type category, folding
   * older devices' locations and history into it. Absent key reads as on.
   */
  readonly combineDevices = signal(localStorage.getItem(COMBINE_DEVICES_KEY) !== 'false');

  setCombineDevices(v: boolean): void {
    localStorage.setItem(COMBINE_DEVICES_KEY, String(v));
    this.combineDevices.set(v);
  }

  setWarningSecs(v: number): void {
    localStorage.setItem(WARN_KEY, String(v));
    this.warningSecs.set(v);
  }

  setLogoutAfterWarningSecs(v: number): void {
    localStorage.setItem(LOGOUT_KEY, String(v));
    this.logoutAfterWarningSecs.set(v);
  }

  setArrowScaleMiles(v: number): void {
    localStorage.setItem(ARROW_SCALE_KEY, String(v));
    this.arrowScaleMiles.set(v);
  }

  resetToDefaults(): void {
    localStorage.removeItem(WARN_KEY);
    localStorage.removeItem(LOGOUT_KEY);
    localStorage.removeItem(ARROW_SCALE_KEY);
    localStorage.removeItem(SYNC_PERIOD_KEY);
    localStorage.removeItem(ALERT_RETENTION_KEY);
    this.warningSecs.set(environment.idleSecondsBeforeWarning);
    this.logoutAfterWarningSecs.set(environment.idleSecondsBeforeLogout);
    this.arrowScaleMiles.set(this.defaultArrowScaleMiles);
    this.syncPeriodSeconds.set(this.defaultSyncPeriodSeconds);
    this.alertRetentionHours.set(this.defaultAlertRetentionHours);
    this.clearHome();
    this.setThemeMode('system');
    this.setCombineDevices(true);
  }
}
